import { newId } from '@/lib/id';
import { normalizeRamq } from '@/lib/ramq';
import { nowIsoSec } from '@/lib/date';

export type PatientStatus = 'active' | 'oriented';

export type Patient = {
  id: string;
  userId: string;
  shiftId: string;
  firstName: string;
  lastName: string;
  dob: string;
  healthCard: string;
  healthCardExp: string;
  status: PatientStatus;
  sex: string;
  civ: string;
  dx: string;
  notes: string;
  flagged: boolean;
  newPatient: boolean;
  createdAt: string;
  amountBilled: string;
  afterShiftTask: boolean;
  transferredToUid?: string;
  transferredAt?: string;
};

export const DEFAULT_DOB = '1950-01-01';
export const DEFAULT_SEX = 'H';

// 'YYYY-MM' → 'YYYY-MM-01' to match Flutter behavior.
export function normalizeRamqExp(exp: string): string {
  const v = exp.trim();
  if (!v) return '';
  return /^\d{4}-\d{2}$/.test(v) ? `${v}-01` : v;
}

export function normalizeDob(dob: string): string {
  return dob.trim() || DEFAULT_DOB;
}

export function normalizeSex(sex: string): string {
  return sex.trim() || DEFAULT_SEX;
}

export function newPatient(args: {
  userId: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  ramq?: string;
  ramqExp?: string;
  sex?: string;
  isNew?: boolean;
}): Patient {
  return {
    id: newId(),
    userId: args.userId,
    shiftId: 'current_shift',
    firstName: (args.firstName ?? '').trim(),
    lastName: (args.lastName ?? '').trim(),
    dob: normalizeDob(args.dob ?? ''),
    healthCard: normalizeRamq(args.ramq),
    healthCardExp: normalizeRamqExp(args.ramqExp ?? ''),
    status: 'active',
    sex: normalizeSex(args.sex ?? ''),
    civ: '',
    dx: '',
    notes: '',
    flagged: false,
    newPatient: args.isNew ?? true,
    createdAt: nowIsoSec(),
    amountBilled: '0',
    afterShiftTask: false,
  };
}

const s = (v: unknown, fallback = ''): string =>
  v == null ? fallback : String(v);

const b = (v: unknown, fallback = false): boolean =>
  typeof v === 'boolean' ? v : fallback;

export function patientFromDoc(id: string, data: Record<string, unknown>): Patient {
  const transferredToUid = s(data.transferredToUid);
  const transferredAt = s(data.transferredAt);
  return {
    id,
    userId: s(data.userId),
    shiftId: s(data.shiftId, 'current_shift'),
    firstName: s(data.firstName),
    lastName: s(data.lastName),
    dob: s(data.dob, DEFAULT_DOB),
    healthCard: s(data.healthCard),
    healthCardExp: s(data.healthCardExp),
    status: (s(data.status, 'active') as PatientStatus) || 'active',
    sex: s(data.sex, DEFAULT_SEX),
    civ: s(data.civ),
    dx: s(data.dx),
    notes: s(data.notes),
    flagged: b(data.flagged),
    newPatient: b(data.newPatient, true),
    createdAt: s(data.createdAt),
    amountBilled: s(data.amountBilled, '0'),
    afterShiftTask: b(data.afterShiftTask),
    ...(transferredToUid ? { transferredToUid } : {}),
    ...(transferredAt ? { transferredAt } : {}),
  };
}

export function patientToMap(p: Patient): Record<string, unknown> {
  const { id: _id, ...rest } = p;
  return rest;
}
